// =============================================================================
// reviewReasons — invoice_run_items.note の翻訳層 (2026-07-08 包括修正)
//
//   workerが書く機械語note (例 "rebuild_failed:gate_blocked:GATE_D_...") を
//   3部構成規約 (何が起きたか業務語 / 影響 / 次アクション1つ) の日本語に写像する。
//   マッチャは順序付き配列を上から評価 (specific → generic)。
//   生noteは raw に保持し、UI側は <details> 折りたたみでのみ表示する。
//
//   E4制約: amount_mismatch / GATE_D は同一入力の再buildが同一gateで再失敗する
//   のが既定のため、自動再試行 (rebuild_item) を出さず instruct のみにする。
// =============================================================================

export type ReasonActionType =
  | 'approve_link'   // #invoice/{月}?ref= ディープリンクへ
  | 'dup_link'       // #duplicate_candidates へ (重複判定)
  | 'rebuild_item'   // POST rebuild-blocked (rowids指定)
  | 'redispatch'     // POST redispatch-failed (rowids/pending対応)
  | 'instruct'       // Claudeへの指示 (createInstruction, 文面プリフィル)
  | 'wait'           // 処理中。待つだけ
  | 'none';          // 終端 (除外・成功)

export interface ReviewReason {
  code: string;                 // 安定識別子 (例 'gate_d_mismatch')
  title: string;                // 業務語見出し
  detail: string;               // 平易な1〜2文。noteから抽出した数値を埋め込む
  action: { type: ReasonActionType; label: string; instructionPrefill?: string };
  severity: 'error' | 'warn' | 'info' | 'ok';
  raw: string;                  // note原文 (折りたたみ表示用)
  autoRetried: boolean;         // '|auto_rebuild_tried' サフィックス検出
}

export interface ReasonCtx { state: string; rowid?: number; proposalRef?: string | null; proposalStatus?: string | null }

const yen = (s: string) => '¥' + Number(s).toLocaleString('ja-JP');

// gate/ルール個別文言 (worker.js 52886-52963 のgate名・RULE名と一致)
const GATE_TEXT: Record<string, string> = {
  GATE_A_fallback_single_forbidden: '明細を1行にまとめる処理が禁止されています',
  GATE_B_reconcile_verdict_unstable: '照合結果が安定しません',
  GATE_C_no_detail_sum: '明細の合計を計算できません',
  GATE_D_detail_vs_total_mismatch: '明細合計と請求額が一致しません(再読取が必要)',
  GATE_E_canonical_stuck: '正規化処理が進みません',
  RULE_7: '部門の指定が必要です',
};
const gateText = (tok: string) => GATE_TEXT[tok] || `仕訳ルール(${tok})に違反しています`;

const prefill = (rowid: number | undefined, body: string) =>
  `rowid ${rowid ?? '◯◯'} ${body}`;

export function translateNote(note: string | null | undefined, ctx: ReasonCtx): ReviewReason {
  const rawFull = String(note || '');
  const autoRetried = /\|auto_rebuild_tried$/.test(rawFull);
  // user_resolve_failed: = 部門ピッカー再作成の失敗。失敗理由部は rebuild_failed と同一語彙のため写像して分類
  // (2026-07-08: 未分類で「詳細不明のエラー」表示になった実バグ)
  const n = rawFull.replace(/\|auto_rebuild_tried$/, '').replace(/^user_resolve_failed:/, 'rebuild_failed:');
  const R = (r: Omit<ReviewReason, 'raw' | 'autoRetried'>): ReviewReason =>
    ({ ...r, raw: rawFull, autoRetried });

  // 成功/除外状態の行は note の内容に関わらずエラー扱いしない
  // (2026-07-08 実バグ: 手動解決した proposed 行の解決メモ「user指示:...」が
  //  未分類 note として「詳細不明のエラー」と誤表示され、完了行に赤エラーが大量出現した)
  if (['proposed', 'proposal_done', 'succeeded'].includes(ctx.state)) {
    const resolved = /^(user指示|user_resolved|rebuilt)/.test(n);
    return R({
      code: 'proposed',
      title: resolved ? '対応済み — 仕訳の下書きができています' : '仕訳の下書きができています',
      detail: resolved
        ? 'あなたの指示で解決済みです。内容を確認して承認すると freee に登録されます。'
        : '内容を確認して承認すると freee に登録されます。',
      action: { type: 'approve_link', label: '承認画面へ' },
      severity: 'ok',
    });
  }
  if (['excluded', 'skipped'].includes(ctx.state)) {
    return R({ code: 'excluded', title: '除外済み', detail: 'この取込の対象外です。', action: { type: 'none', label: '' }, severity: 'info' });
  }

  // note空: state から最低限の理由を生成
  if (!n) {
    const st = ctx.state;
    if (['dispatched', 'queued', 'waiting', 'extracting', 'claimed', 'running', 'extracted'].includes(st)) {
      return R({ code: 'processing', title: '処理中', detail: 'システムが作業中です。待つだけでOKです。', action: { type: 'wait', label: '' }, severity: 'info' });
    }
    if (['proposed', 'proposal_done', 'succeeded'].includes(st)) {
      return R({ code: 'proposed', title: '仕訳の下書きができています', detail: '内容を確認して承認すると freee に登録されます。', action: { type: 'approve_link', label: '承認画面へ' }, severity: 'ok' });
    }
    if (['excluded', 'skipped'].includes(st)) {
      return R({ code: 'excluded', title: '除外済み', detail: 'この取込の対象外です。', action: { type: 'none', label: '' }, severity: 'info' });
    }
    if (st === 'pending') {
      return R({ code: 'pending_no_note', title: '処理を開始できませんでした', detail: '読取が始まっていません。再実行できます。', action: { type: 'redispatch', label: '再実行' }, severity: 'error' });
    }
    return R({ code: 'unknown_empty', title: '詳細不明', detail: '停止理由が記録されていません。AIに調査を依頼できます。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'が理由不明で停止しています。状況を調査して進められるなら proposal 化まで進めてください。') }, severity: 'error' });
  }

  // #1 rebuilt:{provenance}
  if (n.startsWith('rebuilt:')) {
    return R({ code: 'rebuilt', title: '仕訳を作成しました', detail: '再処理で仕訳の下書きができました。承認へ進めます。', action: { type: 'approve_link', label: '承認画面へ' }, severity: 'ok' });
  }
  // #2 user_excluded
  if (n === 'user_excluded') {
    return R({ code: 'user_excluded', title: '除外済み', detail: '取込開始時にあなたが除外した請求書です。', action: { type: 'none', label: '' }, severity: 'info' });
  }
  // #3 deduped_existing_job
  if (n === 'deduped_existing_job') {
    return R({ code: 'deduped', title: '処理中(既存処理に合流)', detail: 'すでに動いている読取処理の結果を待っています。', action: { type: 'wait', label: '' }, severity: 'info' });
  }
  // #4 re_extract_pending|dispatched
  if (/^re_extract_(pending|dispatched)/.test(n)) {
    return R({ code: 're_extract', title: '再読取中', detail: '1回目の読取が不完全だったため、もう一度読み取っています。', action: { type: 'wait', label: '' }, severity: 'info' });
  }
  // #5 dispatch_error: {msg}
  if (n.startsWith('dispatch_error:')) {
    return R({ code: 'dispatch_error', title: '処理を開始できませんでした', detail: 'システム側の一時エラーで読取が始まっていません。再実行できます。', action: { type: 'redispatch', label: '再実行' }, severity: 'error' });
  }
  // #6 job_dead_letter|failed|cancelled
  if (/^job_(dead_letter|failed|cancelled)$/.test(n)) {
    return R({ code: 'job_failed', title: '処理が失敗しました', detail: '読取処理が途中で止まりました。再実行できます。', action: { type: 'redispatch', label: '再実行' }, severity: 'error' });
  }
  // #7 amount_mismatch:header=.. vs sum=../withTax=.. (末尾に日本語サフィックスあり得るため非アンカー)
  {
    const m = n.match(/^amount_mismatch:header=(\d+) vs sum=(\d+)\/withTax=(\d+)/);
    if (m) {
      return R({
        code: 'amount_mismatch', title: '金額が合いません',
        detail: `請求書の合計 ${yen(m[1])} と読み取った明細合計 ${yen(m[2])}(税込 ${yen(m[3])})が一致せず、安全のため自動登録を止めました。`,
        action: { type: 'instruct', label: 'AIに再読取を依頼', instructionPrefill: prefill(ctx.rowid, `の請求書で合計 ${yen(m[1])} と明細合計 ${yen(m[2])} が不一致です。PDFを再読取して正しい明細を確認してください。`) },
        severity: 'error',
      });
    }
  }
  // #8 (build|rebuild)_failed:gate_blocked[:TOKENS]
  {
    const m = n.match(/^(?:build|rebuild)_failed:gate_blocked(.*)$/);
    if (m) {
      const toks = ((m[1] || '').match(/(?:GATE|RULE)_[A-Za-z0-9_]+/g) || []);
      const needsInstruct = toks.some((t) => t.startsWith('GATE_D') || /^RULE_(18c|26|32)/.test(t));
      const isSection = toks.some((t) => t === 'RULE_7');
      const detail = toks.length
        ? toks.map(gateText).join('。') + '。'
        : '仕訳化チェックで停止しました。再作成を試せます。';
      const firstTok = toks[0];
      const code = firstTok ? `gate_${firstTok.toLowerCase()}` : 'gate_generic';
      if (needsInstruct || (autoRetried && !isSection)) {
        return R({
          code, title: '仕訳化チェックで停止', detail,
          action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, `が仕訳化チェック(${toks.join(', ') || 'gate_blocked'})で停止しています。PDFを確認して解消してください。`) },
          severity: 'error',
        });
      }
      return R({
        code, title: '仕訳化チェックで停止', detail,
        action: { type: 'rebuild_item', label: isSection ? '部門補完で再作成' : '再作成を試す' },
        severity: 'error',
      });
    }
  }
  // #10 rebuild_failed:no_vision_cache (#9,#15 の generic より先)
  if (n === 'rebuild_failed:no_vision_cache') {
    return R({ code: 'no_cache', title: '読取データが見つかりません', detail: '読取結果が保存されていません。AIに再読取を依頼してください。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'のvision読取結果が見つかりません。再読取して結果を投入してください。') }, severity: 'error' });
  }
  // #11 extract_empty_twice
  if (n === 'extract_empty_twice') {
    return R({ code: 'empty_twice', title: '2回読み取っても内容が空でした', detail: 'PDFから金額・明細を読み取れませんでした。画像品質の問題の可能性があります。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'は2回の読取とも空でした。PDFを目視確認し、読める場合は手動で結果を投入してください。') }, severity: 'error' });
  }
  // #12 rebuild_failed:no_line_items
  if (n === 'rebuild_failed:no_line_items') {
    return R({ code: 'no_lines', title: '明細を読み取れませんでした', detail: '合計金額はありますが明細行がありません。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'は明細行が読み取れていません。PDFを確認して明細を補完してください。') }, severity: 'error' });
  }
  // #13 rebuild_failed:no_total_amount
  if (n === 'rebuild_failed:no_total_amount') {
    return R({ code: 'no_total', title: '合計金額を特定できませんでした', detail: 'この業者の請求書レイアウトのルールが未整備の可能性があります。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'の合計金額が特定できません。請求書レイアウトのルール整備を検討してください。') }, severity: 'error' });
  }
  // #14 section_unresolved(:lines=...)
  {
    const m = n.match(/^section_unresolved(?::lines=\[?(.*?)\]?)?$/);
    if (m) {
      const lines = (m[1] || '').trim();
      const detail = lines
        ? `どの店舗・部門の費用か過去実績から判定できませんでした。対象明細: ${lines}`
        : 'どの店舗・部門の費用か過去実績から判定できませんでした。';
      if (autoRetried) {
        return R({ code: 'section', title: '部門(店舗)を特定できません', detail, action: { type: 'instruct', label: '部門を指定して依頼', instructionPrefill: prefill(ctx.rowid, `の部門を指定します: 「◯◯」で確定して proposal 化してください。${lines ? `(対象明細: ${lines})` : ''}`) }, severity: 'error' });
      }
      return R({ code: 'section', title: '部門(店舗)を特定できません', detail, action: { type: 'rebuild_item', label: '部門補完で再作成' }, severity: 'error' });
    }
  }
  // #9/#15 build_failed / rebuild_failed (その他)
  if (n.startsWith('build_failed:')) {
    return R({ code: 'build_failed', title: '仕訳を作成できませんでした', detail: '読取は成功しましたが仕訳化で止まりました。再作成を試せます。', action: autoRetried ? { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, `の仕訳化が繰り返し失敗しています(${n})。原因を調査して解消してください。`) } : { type: 'rebuild_item', label: '再作成を試す' }, severity: 'error' });
  }
  if (n.startsWith('rebuild_failed:')) {
    return R({ code: 'rebuild_failed', title: '再作成に失敗しました', detail: '仕訳の再作成でエラーが発生しました。', action: autoRetried ? { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, `の再作成が繰り返し失敗しています(${n})。原因を調査して解消してください。`) } : { type: 'rebuild_item', label: '再作成を試す' }, severity: 'error' });
  }
  // #16 chain_not_fired (手動記入・部分一致)
  if (n.includes('chain_not_fired')) {
    return R({ code: 'chain_stuck', title: '処理が途中で止まっています', detail: '読取は完了していますが、仕訳化への連鎖が止まりました。AIへの確認が必要です。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'の処理連鎖が停止しています(rotation_guard関連)。状況を調査し、proposal 化まで復旧してください。') }, severity: 'error' });
  }
  // #17 duplicate (手動記入 duplicate_rejected(…) と W4新規 dp_duplicate_* の双方)
  if (n.includes('duplicate_rejected') || n.includes('duplicate_review') || n.startsWith('dp_duplicate_')) {
    return R({ code: 'duplicate', title: '重複の疑いがあります', detail: '過去に登録済みの請求書と重複している可能性があると判定されました。', action: { type: 'dup_link', label: '重複判定画面へ', instructionPrefill: prefill(ctx.rowid, 'の重複判定を確認してください。重複でなければ復帰(mark_unique)させてください。') }, severity: 'warn' });
  }
  // #18 dp_rejected (W4新規)
  if (n === 'dp_rejected') {
    return R({ code: 'dp_rejected', title: '却下済みの仕訳があります', detail: '一度作成した仕訳が却下されています。', action: { type: 'instruct', label: 'AIに確認を依頼', instructionPrefill: prefill(ctx.rowid, 'の仕訳が却下済みのまま残っています。再処理すべきか確認してください。') }, severity: 'warn' });
  }
  // #19 vision_blocked:rotation_guard (W4新規)
  if (n === 'vision_blocked:rotation_guard') {
    return R({ code: 'rotation_guard', title: '安全ガードで停止中', detail: '取引先照合の安全ガードにより自動処理を止めています。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, 'が取引先照合の安全ガードで停止しています。状況を調査し復旧してください。') }, severity: 'error' });
  }
  // #20 fallback
  return R({ code: 'unknown', title: '詳細不明のエラー', detail: '分類できないエラーです。原文を添えてAIに調査を依頼できます。', action: { type: 'instruct', label: 'AIに指示', instructionPrefill: prefill(ctx.rowid, `が未分類エラーで停止しています: ${n}`) }, severity: 'error' });
}

// 同一エラーの集約 (サマリーストリップの内訳チップ用)
export function groupReasons(
  items: Array<{ note?: string | null; state: string; rowid?: number }>,
): Array<{ code: string; title: string; count: number }> {
  const map = new Map<string, { code: string; title: string; count: number }>();
  for (const it of items) {
    const r = translateNote(it.note, { state: it.state, rowid: it.rowid });
    const cur = map.get(r.code);
    if (cur) cur.count += 1;
    else map.set(r.code, { code: r.code, title: r.title, count: 1 });
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

// -----------------------------------------------------------------------------
// 実測noteサンプル → 期待code (2026-07-08 run IR260706-l1m7l6 実データで固定)
//   1. 'rebuild_failed:gate_blocked:GATE_D_detail_vs_total_mismatch|auto_rebuild_tried' → gate_gate_d_detail_vs_total_mismatch (instruct)
//   2. 'chain_not_fired(rotation_guard済…)'                                             → chain_stuck (instruct)
//   3. 'section_unresolved:lines=[Indeed PLUS広告配信費…]'                              → section (rebuild_item)
//   4. 'extract_empty_twice'                                                            → empty_twice (instruct)
//   5. 'amount_mismatch:header=2704035 vs sum=2703278/withTax=2919541 (自動隔離)'       → amount_mismatch (instruct)
//   6. 'duplicate_rejected(既存重複判定済)'                                             → duplicate (dup_link)
//   7. 'build_failed:gate_blocked'                                                      → gate_generic (rebuild_item)
// -----------------------------------------------------------------------------
